import React from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { salesPhotosSelector, nurseryIdSelector } from '../../selector';
import { cancelOrder, downloadOrderedPhotoFile } from '../../thunk';

import type { EntityDataPubType } from '@/domain/entityBase';
import type { PhotoAppOrderJson } from '@/types/domain/photo/entities/photoAppOrderEntity';
import type { PhotoOrderJsonPub, PhotoOrderJsonPvt } from '@/types/domain/photo/entities/photoOrderEntity';

import CardMediaImage from '@/components/CardMediaImage';
import DialogTitle from '@/components/DialogTitle';
import DialogTransition from '@/components/DialogTransition';
import MessageBox from '@/components/MessageBox';
import MessageBoxConfirm from '@/components/MessageBoxConfirm';
import PhotoModal from '@/components/PhotoModal';
import { PHOTO_EXPIRED_MONTH, PHOTO_EXPIRED_MONTH_NEW, PHOTO_EXPIRED_MONTH_UPDATED_DATE } from '@/domain/photo/service/entity/photoAppEntity';
import { paymentTypeNameMap } from '@/types/domain/photo/valueObjects/paymentType';
import { dateFormat, isOldVersion } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** 注文 */
  order: EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub>,
  /** 閉じる */
  close: () => void,
};

const getName = (names: string[]) => {
  let text = names[0];
  for (let i = 1; i < names.length; i += 1) {
    text += `, ${names[i]}`;
  }
  return text;
};

type PhotoType = {
  name: string,
  id: string,
  names: string[],
  isDownloadable: boolean,
}

type PhotoNumPriceType = {
  name: string,
  num: number,
  price: number,
}

const getPhotoName = (photo: PhotoAppOrderJson) => {
  if (photo.photoItemId === 'download') {
    return photo.photoItemName;
  }
  return `${photo.photoItemName}(${photo.numberOfItems}枚)`;
};

const PurchaseDetail: React.FC<Props> = ({ order, close }) => {
  const dispatch = useDispatch();
  const salesPhotos = useSelector(salesPhotosSelector);
  const salesPhoto = salesPhotos.find((v) => v.data.salesGroupId === order.data.salesGroupId);
  const photoIds = salesPhoto?.data.photos
    .map((v) => v.photoId).sort((a, b) => (a > b ? 1 : -1)) || [];
  const [isOpenConfirm, setIsOpenConfirm] = React.useState(false);
  const [isOpenCancel, setIsOpenCancel] = React.useState(false);
  const [previewS3Path, setPreviewS3Path] = React.useState<string | undefined>(undefined);
  const [innerWidth, setInnerWidth] = React.useState<number>(window.innerWidth);
  const [_innerHeight, setInnerHeight] = React.useState<number>(window.innerHeight);
  const nurseryId = useSelector(nurseryIdSelector);
  const namesMap: { [key: string]: PhotoType } = {};
  const photoItemMap: { [key: string]: PhotoNumPriceType } = {};
  const now = new Date();
  const dt = new Date(order.data.expirationDate);
  const photoExpiredMonth = isOldVersion(
    order.data.expirationDate,
    PHOTO_EXPIRED_MONTH_UPDATED_DATE,
  ) ? PHOTO_EXPIRED_MONTH : PHOTO_EXPIRED_MONTH_NEW;
  dt.setDate(dt.getDate() + 1);
  dt.setMonth(dt.getMonth() + photoExpiredMonth);
  const isExpired = now > dt;
  const isDownloadable = order.data.photos.findIndex((p) => p.photoItemId === 'download') > -1;
  for (let i = 0; i < order.data.photos.length; i += 1) {
    const id = order.data.photos[i].photoId;
    if (id in namesMap) {
      namesMap[id].names.push(getPhotoName(order.data.photos[i]));
    } else {
      const index = photoIds.indexOf(id);
      namesMap[id] = {
        id,
        name: `No.${index + 1}`,
        names: [getPhotoName(order.data.photos[i])],
        isDownloadable:
          order.data.photos[i].photoItemId === 'download'
          && new Date().getTime() <= dt.getTime(),
      };
    }
    if (order.data.photos[i].photoItemId in photoItemMap) {
      photoItemMap[order.data.photos[i].photoItemId].num += order.data.photos[i].numberOfItems;
      photoItemMap[order.data.photos[i].photoItemId].price
        += order.data.photos[i].numberOfItems * order.data.photos[i].price;
    } else {
      photoItemMap[order.data.photos[i].photoItemId] = {
        name: order.data.photos[i].photoItemName,
        num: order.data.photos[i].numberOfItems,
        price: order.data.photos[i].numberOfItems * order.data.photos[i].price,
      };
    }
  }
  React.useEffect(() => {
    const handleResize = () => {
      setInnerWidth(window.innerWidth);
      setInnerHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const div = Math.floor(innerWidth / 400);
  const width = Math.round((innerWidth - 60 - div * 8) / div);
  return (
    <Dialog
      open
      onClose={() => close()}
      fullScreen
      TransitionComponent={DialogTransition}
    >
      <DialogTitle title="購入の詳細" />
      <DialogContent>
        <div>
          <h3>基本情報</h3>
          <ul>
            <li>{`購入日時：${dateFormat(new Date(order.data.createDatetime), 'yyyy年MM月dd日 HH時mm分ss秒')}`}</li>
            <li>{`支払い金額：${order.data.price}円`}</li>
            <li>{`支払い方法：${paymentTypeNameMap[order.data.paymentType]}`}</li>
            <li>{`支払い状態：${order.data.paymentStatus}`}</li>
            {isDownloadable && (
              <p style={{ color: 'red' }}>{`ダウンロード期限：${dateFormat(dt, 'yyyy年MM月dd日')}`}</p>
            )}
          </ul>
        </div>
        <div>
          <h3>購入写真内訳</h3>
          <ul>
            {Object.keys(photoItemMap).map((key) => {
              const item = photoItemMap[key];
              return (
                <li key={key}>
                  {`${item.name}：${item.num}枚 ${item.price}円`}
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <h3>購入写真一覧</h3>
          {isExpired ? (
            <ul>
              <p style={{ color: 'red' }}>期限を過ぎているため表示できません</p>
            </ul>
          ) : (
            <ImageList cols={div}>
              {Object.keys(namesMap).map((key) => (
                <ImageListItem
                  key={namesMap[key].id}
                  style={{
                    width,
                    border: 'solid 1px #000',
                    backgroundColor: '#aaa',
                  }}
                >
                  <Card style={{ margin: 'auto' }}>
                    <CardMediaImage
                      nurseryId={nurseryId}
                      s3Path={`WagakoNurseryPhoto_PhotoEntity/${namesMap[key].id}/thumbnail.webp`}
                      onClick={() => setPreviewS3Path(`WagakoNurseryPhoto_PhotoEntity/${namesMap[key].id}/sample.webp`)}
                      alt={namesMap[key].id}
                      width={width}
                      objectFit="cover"
                    />
                  </Card>
                  <ImageListItemBar
                    title={getName(namesMap[key].names)}
                    subtitle={namesMap[key].name}
                    actionIcon={namesMap[key].isDownloadable ? (
                      <IconButton
                        style={{ color: 'white' }}
                        aria-label="ダウンロード"
                        onClick={() => {
                          if (order.data.paymentStatus === '支払い済') {
                            dispatch(downloadOrderedPhotoFile({
                              nurseryId,
                              item: order,
                              photoId: namesMap[key].id,
                            }));
                          } else {
                            setIsOpenConfirm(true);
                          }
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    ) : undefined}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </div>
        <div>
          <h3>注文のキャンセル</h3>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsOpenCancel(true)}
            startIcon={<CancelIcon />}
            disabled={order.data.paymentStatus === 'キャンセル'}
          >
            注文キャンセル
          </Button>
          <p>※未支払いの場合のみキャンセル可</p>
        </div>
        {isOpenConfirm && (
          <MessageBoxConfirm
            close={() => {
              setIsOpenConfirm(false);
            }}
            title="支払い処理中です"
            content="施設により支払い処理が完了するまでダウンロードできません。"
          />
        )}
        {isOpenCancel && (
          <MessageBox
            close={(isOk) => {
              if (isOk) {
                dispatch(cancelOrder({
                  order,
                  nurseryId,
                }));
                close();
              }
              setIsOpenCancel(false);
            }}
            title="注文のキャンセル"
            content="注文をキャンセルしますか？この処理は戻せません。"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={() => close()}>
          閉じる
        </Button>
      </DialogActions>
      {previewS3Path && (
        <PhotoModal
          s3Path={previewS3Path}
          close={() => setPreviewS3Path(undefined)}
          nurseryId={nurseryId}
        />
      )}
    </Dialog>
  );
};

export default PurchaseDetail;
